// Main App component for Oma frontend

import { useState, useEffect } from 'react';
import { OmaMark } from './components/OmaMark';
import { OpeningQuestion } from './components/OpeningQuestion';
import { ConversationThread } from './components/ConversationThread';
import { MessageInput } from './components/MessageInput';
import { CapacityIndicator } from './components/CapacityIndicator';
import { ExtendCapacityModal } from './components/ExtendCapacityModal';
import { api } from './services/api';
import type { Message, CapacityLevel, CapacityState } from './types';
import './styles/global.css';

function App() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [openingQuestion, setOpeningQuestion] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isQuestionLoading, setIsQuestionLoading] = useState(true);
  const [capacity, setCapacity] = useState<CapacityState>({
    level: 'sustained',
    energyLevel: 50,
    message: 'I have adequate capacity for sustained exploration.'
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [walletAddresses] = useState({
    btc: 'bc1q6cttfn6jhu3tqm8qmptarmqgk4gls02r3nh0rw',
    eth: '0x576189574b60fF4ADb435c91d3Cf0c9Bd41A2d3F'
  });

  useEffect(() => {
    loadOpeningQuestion();
    loadInfo();
  }, []);

  const loadOpeningQuestion = async () => {
    setIsQuestionLoading(true);
    const response = await api.getOpeningQuestion();
    if (response.success && response.data) {
      setOpeningQuestion(response.data.question);
    }
    setIsQuestionLoading(false);
  };

  const loadInfo = async () => {
    const response = await api.getInfo();
    if (response.success && response.data) {
      const { energyLevel } = response.data;
      updateCapacityFromEnergy(energyLevel);
    }
  };

  const updateCapacityFromEnergy = (energyLevel: number) => {
    let level: CapacityLevel = 'sustained';
    let message = 'I have adequate capacity for sustained exploration.';
    
    if (energyLevel >= 75) {
      level = 'deep';
      message = 'I have the capacity to explore this with you at depth.';
    } else if (energyLevel >= 50) {
      level = 'sustained';
      message = 'I have adequate capacity for sustained exploration.';
    } else if (energyLevel >= 25) {
      level = 'limited';
      message = 'My capacity is somewhat limited right now.';
    } else {
      level = 'minimal';
      message = 'I can engage, but my analysis will be more focused.';
    }
    
    setCapacity({ level, energyLevel, message });
  };

  const handleSendMessage = async (messageContent: string) => {
    if (isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageContent,
      timestamp: Date.now()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await api.sendMessage(messageContent, conversationId);
      
      if (response.success && response.data) {
        const { response: omaResponse, energyLevel, conversationId: newConversationId } = response.data;
        
        const omaMessage: Message = {
          id: `oma-${Date.now()}`,
          role: 'oma',
          content: omaResponse,
          timestamp: Date.now()
        };
        
        setMessages(prev => [...prev, omaMessage]);
        setConversationId(newConversationId);
        updateCapacityFromEnergy(energyLevel);
      } else {
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          role: 'oma',
          content: 'I apologize, but I encountered a difficulty processing your message. Please try again.',
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'oma',
        content: 'I apologize, but I encountered a technical difficulty. Please try again.',
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative'
    }}>
      <main style={{
        flex: 1,
        padding: 'var(--space-xl) 0 var(--space-5xl)'
      }}>
        <OmaMark capacity={capacity.level} />
        
        <OpeningQuestion 
          question={openingQuestion} 
          isLoading={isQuestionLoading}
        />
        
        <ConversationThread messages={messages} />
        
        <MessageInput 
          onSendMessage={handleSendMessage}
          disabled={isLoading}
          placeholder={isLoading ? "Thinking..." : "Your response..."}
        />
      </main>
      
      <CapacityIndicator 
        capacity={capacity.level}
        onExtendCapacity={() => setIsModalOpen(true)}
      />
      
      <ExtendCapacityModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        btcAddress={walletAddresses.btc}
        ethAddress={walletAddresses.eth}
      />
    </div>
  );
}

export default App;
